import { useEffect, useMemo, useState } from 'react';

import {
  DEVICE_AVAILABILITY_MAX_DEVICES,
  DEVICE_AVAILABILITY_MAX_RANGE_DAYS,
  DEVICE_AVAILABILITY_NOW_REFRESH_MS,
} from '../../constants/device-availability.js';
import { MS_PER_DAY } from '../../constants/suppressions.js';
import { useIntl } from '../../hooks';
import {
  type AvailabilityState,
  type DeviceAvailability,
  isExpectedAvailabilityRejection,
  useDeviceAvailability,
} from '../../hooks/useDeviceAvailability.js';
import { extractErrorMessage, getApiErrorStatus } from '../../lib/error.js';
import { formatLongDuration } from '../../lib/format-duration.js';
import { cn } from '../../lib/utils';
import type { Device } from '../../types/device.js';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Loading } from '../ui';

type AvailabilityWindow = {
  deviceIds: string[];
  from?: string;
  to?: string;
};

type DeviceAvailabilityPanelProps = AvailabilityWindow & {
  devices: Device[];
};

export const getAvailabilityBlockReason = (
  { deviceIds, from, to }: AvailabilityWindow,
  now: number,
):
  | 'needsDevices'
  | 'needsFrom'
  | 'tooManyDevices'
  | 'fromInFuture'
  | 'toBeforeFrom'
  | 'rangeTooLong'
  | null => {
  if (deviceIds.length === 0) return 'needsDevices';
  if (!from) return 'needsFrom';
  if (deviceIds.length > DEVICE_AVAILABILITY_MAX_DEVICES) return 'tooManyDevices';

  const fromMs = new Date(from).getTime();
  if (fromMs >= now) return 'fromInFuture';
  const end = Math.min(to ? new Date(to).getTime() : now, now);
  if (end <= fromMs) return 'toBeforeFrom';
  if (end - fromMs > DEVICE_AVAILABILITY_MAX_RANGE_DAYS * MS_PER_DAY) return 'rangeTooLong';
  return null;
};

const SEGMENT_COLORS: Record<AvailabilityState, string> = {
  online: 'bg-success',
  offline: 'bg-destructive',
  nodata: 'bg-muted',
};

const collapseTimeline = (timeline: AvailabilityState[]) =>
  timeline.reduce<{ state: AvailabilityState; count: number }[]>((segments, state) => {
    const last = segments[segments.length - 1];
    if (last?.state === state) last.count += 1;
    else segments.push({ state, count: 1 });
    return segments;
  }, []);

const compareByAvailability = (
  first: { availability: DeviceAvailability; label: string },
  second: { availability: DeviceAvailability; label: string },
) => {
  const firstPct = first.availability.availabilityPct;
  const secondPct = second.availability.availabilityPct;
  if (firstPct === secondPct) return first.label.localeCompare(second.label);
  if (firstPct === null) return 1;
  if (secondPct === null) return -1;
  return firstPct - secondPct;
};

const REJECTION_MESSAGE_IDS: Partial<Record<number, string>> = {
  403: 'analytics.deviceAvailability.noAccess',
  404: 'analytics.deviceAvailability.notFound',
  422: 'analytics.deviceAvailability.narrowPeriod',
};

const formatPct = (pct: number | null) => (pct === null ? '—' : `${pct.toFixed(1)}%`);

function TimelineBar({ timeline, label }: { timeline: AvailabilityState[]; label: string }) {
  return (
    <div
      role="img"
      aria-label={label}
      className="flex h-3 w-full overflow-hidden rounded-sm bg-muted"
    >
      {collapseTimeline(timeline).map(({ state, count }, index) => (
        <div
          key={index}
          data-testid="device-availability-segment"
          data-state={state}
          className={cn('h-full', SEGMENT_COLORS[state])}
          style={{ flexGrow: count }}
        />
      ))}
    </div>
  );
}

export function DeviceAvailabilityPanel({
  deviceIds,
  from,
  to,
  devices,
}: DeviceAvailabilityPanelProps) {
  const { formatMessage } = useIntl();
  // Date.now() is impure in render, so the window is judged against a sampled timestamp; while the
  // window still reaches past now it re-samples, so a future `from` unblocks and a drifting span is
  // judged fresh.
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    setNow(Date.now());
    if (to && new Date(to).getTime() <= Date.now()) return undefined;
    const refreshTimer = setInterval(() => setNow(Date.now()), DEVICE_AVAILABILITY_NOW_REFRESH_MS);
    return () => clearInterval(refreshTimer);
  }, [deviceIds, from, to]);
  const reason = getAvailabilityBlockReason({ deviceIds, from, to }, now);

  const {
    data: availabilityResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useDeviceAvailability({ deviceIds, from: from ?? '', to }, { enabled: reason === null });

  const availabilityRows = useMemo(() => {
    const serialById = new Map(devices.map((device) => [device.id, device.serial]));
    return (availabilityResponse?.devices ?? [])
      .map((availability) => ({
        availability,
        label: serialById.get(availability.deviceId) ?? availability.deviceId,
      }))
      .sort(compareByAvailability);
  }, [availabilityResponse, devices]);

  const rowsWithData = availabilityRows.filter(
    ({ availability }) => availability.availabilityPct !== null,
  );
  const worst = rowsWithData[0];
  const average = worst
    ? rowsWithData.reduce((sum, { availability }) => sum + (availability.availabilityPct ?? 0), 0) /
      rowsWithData.length
    : null;

  const renderBody = () => {
    if (reason) {
      return (
        <p data-testid="device-availability-hint" className="text-sm text-muted-foreground">
          {formatMessage(
            { id: `analytics.deviceAvailability.${reason}` },
            {
              max:
                reason === 'tooManyDevices'
                  ? DEVICE_AVAILABILITY_MAX_DEVICES
                  : DEVICE_AVAILABILITY_MAX_RANGE_DAYS,
            },
          )}
        </p>
      );
    }
    if (isLoading)
      return <Loading text={formatMessage({ id: 'analytics.deviceAvailability.loading' })} />;
    if (error) {
      const status = getApiErrorStatus(error);
      const failedText = formatMessage({ id: 'analytics.deviceAvailability.failed' });
      const rejectionMessageId = status === undefined ? undefined : REJECTION_MESSAGE_IDS[status];
      const errorText =
        status === 400
          ? extractErrorMessage(error, failedText)
          : rejectionMessageId
            ? formatMessage({ id: rejectionMessageId })
            : failedText;
      const isRetryable = !isExpectedAvailabilityRejection(error);
      return (
        <div data-testid="device-availability-error" className="flex flex-col items-start gap-2">
          <p className="text-sm text-destructive">{errorText}</p>
          {isRetryable && (
            <Button
              variant="outline"
              size="sm"
              data-testid="device-availability-retry"
              disabled={isFetching}
              onClick={() => refetch()}
            >
              {formatMessage({
                id: isFetching
                  ? 'analytics.deviceAvailability.retrying'
                  : 'analytics.deviceAvailability.retry',
              })}
            </Button>
          )}
        </div>
      );
    }
    if (availabilityRows.length === 0) {
      return (
        <p data-testid="device-availability-empty" className="text-sm text-muted-foreground">
          {formatMessage({ id: 'analytics.deviceAvailability.empty' })}
        </p>
      );
    }
    return (
      <>
        <div data-testid="device-availability-summary" className="flex flex-col gap-1 text-sm">
          {worst ? (
            <>
              <p>
                {formatMessage(
                  { id: 'analytics.deviceAvailability.average' },
                  { value: formatPct(average) },
                )}
              </p>
              <p>
                {formatMessage(
                  { id: 'analytics.deviceAvailability.worst' },
                  { label: worst.label, value: formatPct(worst.availability.availabilityPct) },
                )}
              </p>
            </>
          ) : (
            <p data-testid="device-availability-nodata" className="text-muted-foreground">
              {formatMessage({ id: 'analytics.deviceAvailability.noData' })}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-3">
          {availabilityRows.map(({ availability, label }) => (
            <div
              key={availability.deviceId}
              data-testid="device-availability-row"
              data-device-id={availability.deviceId}
              className="flex flex-col gap-1"
            >
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="font-medium">{label}</span>
                <span>{formatPct(availability.availabilityPct)}</span>
              </div>
              <TimelineBar
                timeline={availability.timeline}
                label={formatMessage(
                  { id: 'analytics.deviceAvailability.timelineLabel' },
                  { label },
                )}
              />
              <div className="flex flex-wrap gap-x-4 text-xs text-muted-foreground">
                <span data-testid="device-availability-disconnects">
                  {formatMessage({ id: 'analytics.deviceAvailability.disconnects' })}:{' '}
                  {availability.disconnects}
                </span>
                <span>
                  {formatMessage({ id: 'analytics.deviceAvailability.downtime' })}:{' '}
                  {formatLongDuration(availability.offlineMs)}
                </span>
                <span>
                  {formatMessage({ id: 'analytics.deviceAvailability.longestDowntime' })}:{' '}
                  {formatLongDuration(availability.longestOfflineMs)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <Card data-testid="device-availability-panel">
      <CardHeader>
        <CardTitle className="text-lg">
          {formatMessage({ id: 'analytics.deviceAvailability.title' })}
        </CardTitle>
        <CardDescription>
          {formatMessage({ id: 'analytics.deviceAvailability.description' })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">{renderBody()}</CardContent>
    </Card>
  );
}
